import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { FormattedMessage } from 'react-intl';
import { Divider } from '@katalon-studio/katalon-ui/v2';
import { Link as RouterLink } from 'react-router-dom';
import { Panel } from '../../../../components/common/Panel';

const FEATURE_CHIPS = [
  'page.true.platform.welcome.chip.ai.agents',
  'page.true.platform.welcome.chip.manual.testing',
  'page.true.platform.welcome.chip.cloud.execution',
  'page.true.platform.welcome.chip.test.management',
  'page.true.platform.welcome.chip.reporting',
];

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(4),
    backgroundColor: '#fff',
    borderRadius: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  leftColumn: {
    flex: 1,
  },
  rightColumn: {
    flex: 1,
  },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  checkBadge: {
    width: theme.spacing(4),
    height: theme.spacing(4),
    borderRadius: theme.spacing(1),
    backgroundColor: '#5959EB',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  checkBadgeIcon: {
    color: '#fff',
    fontSize: 14,
    fontWeight: 700,
  },
  statusLabel: {
    fontSize: theme.spacing(2),
    fontWeight: 800,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    color: '#5959EB',
  },
  mainTitle: {
    color: '#1A1A3E',
    marginBottom: theme.spacing(2),
  },
  description: {
    fontSize: theme.spacing(2),
    color: '#4A4A6A',
    marginBottom: theme.spacing(2.5),
    lineHeight: 1.6,
  },
  chipsContainer: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2.5),
  },
  featureChip: {
    backgroundColor: '#F0F0FF',
    color: '#5959EB',
    fontWeight: 600,
    '& .MuiChip-label': {
      padding: theme.spacing(0.25, 1.25),
    },
    borderRadius: theme.spacing(2),
  },
  firstWaveText: {
    fontSize: theme.spacing(1.875),
    color: '#6B6B8A',
    lineHeight: 1.5,
  },
  whatHappensBox: {
    backgroundColor: '#EEF0F9',
    borderRadius: theme.spacing(1.5),
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
  },
  whatHappensLabel: {
    fontSize: theme.spacing(1.5),
    fontWeight: 700,
    letterSpacing: '0.1em',
    color: '#5959EB',
    textTransform: 'uppercase' as const,
    marginBottom: theme.spacing(1.5),
  },
  nextItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
  nextItemIcon: {
    fontSize: theme.spacing(3),
    flexShrink: 0,
    marginTop: 2,
  },
  nextItemTitle: {
    fontWeight: 700,
    fontSize: theme.spacing(2),
    color: '#1A1A3E',
    marginBottom: theme.spacing(0.5),
  },
  nextItemDesc: {
    fontSize: theme.spacing(1.75),
    color: '#6B6B8A',
    lineHeight: 1.5,
  },
  exploreButton: {
    width: '100%',
    fontWeight: 700,
    fontSize: theme.spacing(2),
    padding: theme.spacing(1.25, 3),
    borderRadius: theme.spacing(0.75),
    textTransform: 'none' as const,
    backgroundColor: '#5959EB',
    color: '#fff',
    marginTop: theme.spacing(2),
    '&:hover': {
      backgroundColor: '#43509B',
    },
  },
}));

export interface TruePlatformWelcomeProps {
  renewalDate?: Date;
}

export const TruePlatformWelcome = ({ renewalDate }: TruePlatformWelcomeProps) => {
  const classes = useStyles();
  const isRenewal = renewalDate !== undefined;

  const immediateNextItems = [
    {
      key: 'explore',
      titleId: 'page.true.platform.welcome.immediate.next.explore.title',
      descId: 'page.true.platform.welcome.immediate.next.explore.desc',
    },
    {
      key: 'manage',
      titleId: 'page.true.platform.welcome.immediate.next.manage.title',
      descId: 'page.true.platform.welcome.immediate.next.manage.desc',
    },
  ];

  const renewalNextItems = [
    {
      key: 'current',
      titleId: 'page.true.platform.welcome.renewal.next.current.title',
      descId: 'page.true.platform.welcome.renewal.next.current.desc',
    },
    {
      key: 'autorenew',
      titleId: 'page.true.platform.welcome.renewal.next.autorenew.title',
      descId: 'page.true.platform.welcome.renewal.next.autorenew.desc',
    },
  ];

  const nextItems = isRenewal ? renewalNextItems : immediateNextItems;

  const resolveExplorePath = () => {
    if (isRenewal) {
      return process.env.REACT_APP_TRUE_CENTER_URL ?? 'https://katalon.com/true-information-center';
    }
    return process.env.REACT_APP_PLATFORM_KATALON_AI ?? 'https://platform.katalon.com/katalon-ai';
  };

  return (
    <Panel
      id="plan-v2-welcome"
      sx={theme => ({
        margin: theme.spacing(4, 0),
      })}
      border
    >
      <Box className={classes.root}>
        {/* Left column */}
        <Box
          p={4}
          pr={2}
          className={classes.leftColumn}
        >
          {/* Status row */}
          <Box className={classes.statusRow}>
            <Typography
              className={classes.statusLabel}
            >
              <FormattedMessage
                id={isRenewal
                  ? 'page.true.platform.welcome.renewal.status'
                  : 'page.true.platform.welcome.immediate.status'}
              />
            </Typography>
          </Box>

          {/* Main title */}
          <Box className={classes.mainTitle}>
            <Typography fontWeight={800} fontSize={24}>
              <FormattedMessage
                id={isRenewal
                  ? 'page.true.platform.welcome.renewal.title'
                  : 'page.true.platform.welcome.immediate.title'}
              />
            </Typography>
            {isRenewal && (
              <Typography fontWeight={800} fontSize={24}>
                <FormattedMessage
                  id="page.true.platform.welcome.renewal.post.title"
                />
              </Typography>
            )}
          </Box>

          {/* Description */}
          <Typography className={classes.description}>
            <FormattedMessage
              id={isRenewal
                ? 'page.true.platform.welcome.renewal.desc'
                : 'page.true.platform.welcome.immediate.desc'}
            />
          </Typography>

          {/* Feature chips */}
          <Box className={classes.chipsContainer}>
            {FEATURE_CHIPS.map(chipId => (
              <Chip
                key={chipId}
                label={(
                  <Typography fontWeight={600} fontSize={13}>
                    <FormattedMessage id={chipId} />
                  </Typography>
                )}
                className={classes.featureChip}
                icon={<FiberManualRecordIcon sx={{ fontSize: '6px !important', color: '#5959EB !important' }} />}
              />
            ))}
          </Box>

          {/* First wave text */}
          <Typography className={classes.firstWaveText}>
            <FormattedMessage id="page.true.platform.welcome.first.wave.text" />
          </Typography>
        </Box>

        <Divider
          id="true-platform-upgrade-divider"
          orientation="vertical"
          sx={{
            width: '1px',
            backgroundColor: '#fafafa',
            flexDirection: 'column',
          }}
          flexItem
        />

        {/* Right column */}
        <Box
          className={classes.rightColumn}
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          p={4}
          pl={2}
        >
          <Box className={classes.whatHappensBox}>
            <Typography className={classes.whatHappensLabel}>
              <FormattedMessage id="page.true.platform.welcome.what.happens.next" />
            </Typography>

            {nextItems.map(item => (
              <Box key={item.key} className={classes.nextItem}>
                <Box>
                  <Typography className={classes.nextItemTitle}>
                    <FormattedMessage id={item.titleId} />
                  </Typography>
                  <Typography className={classes.nextItemDesc}>
                    <FormattedMessage id={item.descId} />
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          <Button
            id="true-platform-explore-btn"
            size="large"
            className={classes.exploreButton}
            component={RouterLink}
            to={resolveExplorePath()}
            target="_blank"
          >
            <FormattedMessage id="page.true.platform.welcome.explore.btn" />
            {' →'}
          </Button>
        </Box>
      </Box>
    </Panel>
  );
};
